import { ProductItemInterface, StoreInterface } from "@/assets/interfaces";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReviewCard from "./review";
import ProductGallery from "@/components/ui/product-gallery";
import useTabs from "@/components/hooks/useTabs";
import CartContext from "@/contexts/cart-context";
import { useModals } from "@/components/hooks/useModals";
import { capitalizeFirstLetter } from "@/assets/js/utils/functions";
import classNames from "classnames";
import RelatedProducts from "./related-products";
import useLockPageScroll from "@/components/hooks/useLockPageScroll";
import ProductInfo from "../modals/product-details/product-info";
import ProductOptions from "../modals/product-details/product-options";
import QuantitySelector from "../modals/product-details/quantity-selector";
import ActionButtons from "../modals/product-details/action-buttons";
import InfoBlocksSection from "../modals/product-details/info-blocks";
import ImageWithPlaceholder from "@/components/ui/image-with-placeholder";
import { AppBtn } from "@/components/ui/buttons";
import { GetRelatedItems } from "@/api/items";
import { useRequest } from "@/api/utils";
import Portal from "@/components/portal";
import ShareProductModal from "../modals/share-product";
import Router, { useRouter } from "next/router";
import CartContextV2 from "@/contexts/cart-context-v2";
import StorefrontContext from "@/contexts/storefront";

type Props = {
  item: ProductItemInterface;
  productOnly?: boolean;
  store: StoreInterface;
};

function ProductDetailsSection({ item, productOnly = false, store }: Props) {
  const { modals, toggleModal } = useModals(["image_carousel", "share_product"]);
  const router = useRouter();
  const [currentImage, setCurrentImage] = useState(0);
  const {
    addOrRemoveItem,
    isItemInCart,
    cart,

    toggleModal: toggleModalFromCart,
    removeItem,
    updateItemQuantity,
    setQuantity,
    getItemQuantity,
    formatAsCurrency,
  } = CartContextV2.useContainer();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const descBodyRef = useRef<HTMLDivElement>(null);
  const tabs = useTabs(["Product Details", "Customer Reviews"], 0);
  const [qty, setQty] = useState<number>(1);
  const discountExists = item.discount_price !== undefined && item.discount_price > 0;
  const [related, setRelated] = useState<ProductItemInterface[]>([]);
  const relatedData = useRequest(GetRelatedItems);

  const getRelated = useCallback(async () => {
    const [res, err] = await relatedData.makeRequest({ id: item?.id, limit: 5 });

    if (res) {
      const data = res.data;
      setRelated(data);
    }
    if (err) {
      console.log(err);
    }
  }, [item?.id]);

  const handleItemButtonClick = (item: ProductItemInterface) => {
    var variants = [];
    addOrRemoveItem(item, variants, [qty]);
    setQuantity(item.id, qty);
    setQty(1);
  };

  const setSelectedItem = (item: ProductItemInterface) => {
    const currentPath = router.asPath;

    // If we're on a direct product page (starts with /products/),
    // navigate to the related product with the same prefix
    if (currentPath.startsWith("/products/")) {
      Router.push(`/products/${item?.slug}`);
    } else {
      // For storefront navigation, just use the slug
      Router.push("/" + item?.slug);
    }
  };

  useEffect(() => {
    getRelated();
  }, [getRelated]);
  return (
    <div
      className={classNames(
        ["w-full py-4.5 px-5 sm:px-9 md:px-0", productOnly],
        ["w-full block space-y-6 sm:space-y-8 md:space-y-15 mx-auto store-container px-5 sm:px-9 md:px-0", !productOnly]
      )}
    >
      <div className="store-container space-y-6 sm:space-y-8 md:space-y-15">
        <div
          className={`flex flex-col lg:flex-row gap-8 md:gap-11.5 items-stretch ${
            item?.images?.length + item?.videos?.length > 1 ? "lg:aspect-[1/0.54]" : "lg:aspect-[2/1]"
          }`}
        >
          <div className="w-full flex-1 min-w-[350px] h-full">
            <div className="w-full max-w-[500px] lg:max-w-full bg-white flex flex-col justify-center items-center">
              <ProductGallery
                shareAction={() => toggleModal("share_product")}
                images={item?.images}
                videos={item?.videos}
              />
            </div>
          </div>
          <div
            ref={descBodyRef}
            className={`w-full h-full flex-1 flex flex-col gap-3.75 min-w-[350px] relative overflow-y-auto scrollbar-hide pb-5`}
          >
            <div className="flex-1">
              <ProductInfo item={item} formatAsCurrency={formatAsCurrency} />
              <ProductOptions item={item} onSelectOptions={() => addOrRemoveItem(item)} />
              <div className="flex items-center w-full justify-between space-x-3 mt-6">
                <AppBtn className="flex-1" size="md" onClick={() => handleItemButtonClick(item)}>
                  Buy this item
                </AppBtn>
                <AppBtn
                  color="neutral"
                  className="!text-accent-red-500 text-primary-500 flex-1"
                  isBlock
                  href={`/${store?.slug}/products`}
                >
                  Browse More Items
                </AppBtn>
              </div>
              <InfoBlocksSection infoBlocks={item?.info_blocks} />

              <div className="bg-grey-fields-200 rounded-10 p-5 mt-6 space-y-3.75">
                <div className="flex items-center justify-between">
                  <figure className="">
                    {store?.has_paid_subscription ? (
                      <ImageWithPlaceholder
                        image={store?.logo}
                        text={store?.name}
                        className="h-10 w-10 text-xl sm:text-2lg font-bold"
                      />
                    ) : (
                      <img src="/images/logos/primary.svg" alt="" className="h-5 sm:h-5.5 md:h-6" />
                    )}
                  </figure>
                  <AppBtn
                    color="white"
                    className="rounded-[50px!important] border border-grey-divider text-primary-500"
                    size="sm"
                    href={`/${store?.slug}`}
                  >
                    View Store
                    {/* prettier ignore */}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M5.87563 5.87521H14.1252M14.1252 5.87521V14.1248M14.1252 5.87521L5.87563 14.1248"
                        stroke="currentColor"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </AppBtn>
                </div>
                <div className="space-y-1">
                  <h4 className="text-black text-left text-[20px] font-semibold flex-1 overflow-hidden whitespace-nowrap overflow-ellipsis mr-5">
                    {store?.name}
                  </h4>
                  <p className="text-black text-xs md:text-sm mb-2.5">{store?.description}</p>
                </div>
              </div>
            </div>

            {/* <div className="fixed z-[50] lg:z-auto lg:sticky w-full bottom-0 left-0 right-0 flex flex-col gap-2 px-5 md:px-9 lg:px-0 py-5 bg-white border-t border-grey-border border-opacity-50 shadow-top">
                    {isItemInCart(item?.id) && cartItem ? (
                      <ActionButtons
                        isInCart={true}
                        onAddToCart={() => addOrRemoveItem(item)}
                        onOpenCart={() => toggleModalFromCart("cart")}
                        onCloseModal={() => toggleModalFromCart("cart")}
                      />
                    ) : (
                      <>
                        {(!item?.variants || !item?.variants?.options || item?.variants?.options.length < 1) && (
                          <QuantitySelector quantity={qty} setQuantity={setQty} maxQuantity={item?.quantity} />
                        )}

                        <ActionButtons
                          isInCart={false}
                          onAddToCart={() => handleItemButtonClick(item)}
                          onOpenCart={() => toggleModalFromCart("cart")}
                          onCloseModal={() => toggleModalFromCart("cart")}
                        />
                      </>
                    )}
                  </div> */}
          </div>
        </div>
        <RelatedProducts
          items={relatedData?.response?.data}
          classNames="pb-30"
          noPadding={true}
          productsLoading={relatedData.isLoading}
          setSelectedItem={setSelectedItem}
        />
      </div>
      <Portal>
        <ShareProductModal
          show={modals.share_product}
          toggle={() => toggleModal("share_product")}
          productSlug={item?.slug}
        />
      </Portal>
    </div>
  );
}

export default ProductDetailsSection;
